<template>
  <div class="home">
    <Header />
    
    <div class="home-content">
    <!-- 轮播图 -->
    <div v-if="loadingBanners" class="banner-skeleton" :style="{ height: carouselHeight }">
      <el-skeleton :rows="0" animated>
        <template slot="template">
          <el-skeleton-item variant="rect" style="width: 100%; height: 100%;" />
        </template>
      </el-skeleton>
    </div>
    <el-carousel v-else :interval="5000" arrow="hover" :height="carouselHeight">
      <el-carousel-item v-for="(item, index) in banners" :key="index">
        <div 
          class="banner-item" 
          :style="{ backgroundImage: `url(${getImageUrl(item.image)})` }"
          @error="handleImageError"
        >
          <div class="banner-content">
            <h2>{{ item.title }}</h2>
            <p>{{ item.subtitle }}</p>
          </div>
        </div>
      </el-carousel-item>
    </el-carousel>

    <!-- 报价表单 -->
    <div class="quote-section">
      <div class="container">
        <QuoteForm />
      </div>
    </div>

    <!-- 服务项目 -->
    <div class="services-section">
      <div class="container">
        <h2 class="section-title">服务项目</h2>
        <p class="section-subtitle">专业轿车托运，以快递的标准做物流</p>
        <el-row :gutter="30">
          <el-col :xs="24" :sm="12" :md="6" v-for="service in services" :key="service.id">
            <div class="service-card">
              <div class="service-image">
                <img :src="service.image" :alt="service.title">
              </div>
              <h3>{{ service.title }}</h3>
              <p>{{ service.desc }}</p>
              <div class="service-feature">
                <span>赠送 {{ service.insurance }}万车辆运输保险</span>
              </div>
              <el-button type="primary" @click="goToServices">获取报价</el-button>
            </div>
          </el-col>
        </el-row>
      </div>
    </div>

    <!-- 运车流程 -->
    <div class="process-section">
      <div class="container">
        <h2 class="section-title">运车流程</h2>
        <p class="section-subtitle">标准化运车流程，一站式门到门服务</p>
        <el-steps :active="6" finish-status="success" align-center>
          <el-step title="签订协议" description="运输协议赠送保险"></el-step>
          <el-step title="预约交车" description="提供时间地点"></el-step>
          <el-step title="检查车辆" description="车辆外观内饰检查"></el-step>
          <el-step title="安全运输" description="全程GPS定位跟踪"></el-step>
          <el-step title="接车验车" description="检查无误联系接车"></el-step>
          <el-step title="支付运费" description="满意再付款"></el-step>
        </el-steps>
      </div>
    </div>

    <!-- 服务优势 -->
    <div class="advantages-section">
      <div class="container">
        <h2 class="section-title">服务优势</h2>
        <el-row :gutter="30">
          <el-col :xs="24" :sm="12" :md="8" v-for="advantage in advantages" :key="advantage.id">
            <div class="advantage-item">
              <div class="advantage-image">
                <img 
                  :src="advantage.image" 
                  :alt="advantage.title" 
                  @error="handleAdvantageImageError"
                  @load="handleAdvantageImageLoad"
                >
              </div>
              <h3>{{ advantage.title }}</h3>
              <p>{{ advantage.desc }}</p>
            </div>
          </el-col>
        </el-row>
      </div>
    </div>

    <!-- 运车案例 -->
    <div class="cases-section">
      <div class="container">
        <h2 class="section-title">运车案例</h2>
        <el-row :gutter="30" v-if="loadingCases">
          <el-col :xs="24" :sm="12" :md="6" v-for="i in 4" :key="i">
            <div class="case-card">
              <el-skeleton :rows="0" animated>
                <template slot="template">
                  <!-- case-header 骨架 -->
                  <div class="case-header" style="margin-bottom: 15px; display: flex; justify-content: space-between;">
                    <el-skeleton-item variant="text" style="width: 60%;" />
                    <el-skeleton-item variant="text" style="width: 35%;" />
                  </div>
                  <!-- case-info 骨架 -->
                  <div class="case-info" style="margin-bottom: 15px;">
                    <el-skeleton-item variant="text" style="width: 100%; margin-bottom: 8px;" />
                    <el-skeleton-item variant="text" style="width: 85%;" />
                  </div>
                  <!-- case-comment 骨架 -->
                  <div class="case-comment">
                    <el-skeleton-item variant="text" style="width: 100%; margin-bottom: 8px;" />
                    <el-skeleton-item variant="text" style="width: 95%; margin-bottom: 8px;" />
                    <el-skeleton-item variant="text" style="width: 80%;" />
                  </div>
                </template>
              </el-skeleton>
            </div>
          </el-col>
        </el-row>
        <el-row v-else :gutter="30">
          <el-col :xs="24" :sm="12" :md="6" v-for="caseItem in cases" :key="caseItem.id">
            <div class="case-card">
              <div class="case-header">
                <span class="case-route">{{ caseItem.from }} → {{ caseItem.to }}</span>
                <span class="case-time">历时{{ caseItem.days }}天</span>
              </div>
              <div class="case-info">
                <p class="case-car">{{ caseItem.car }}</p>
                <p class="case-value">估值{{ caseItem.value }}万 全额太平洋保险</p>
              </div>
              <div class="case-comment">
                <p>车主评价：{{ caseItem.comment }}</p>
              </div>
            </div>
          </el-col>
        </el-row>
        <div class="text-center" style="margin-top: 30px;">
          <el-button type="primary" @click="$router.push('/cases')">查看更多案例</el-button>
        </div>
      </div>
    </div>

    <!-- 新闻资讯 -->
    <div class="news-section">
      <div class="container">
        <h2 class="section-title">新闻资讯</h2>
        <el-row :gutter="30" v-if="loadingNews">
          <el-col :xs="24" :sm="12" :md="8" v-for="i in 3" :key="i">
            <div class="news-card">
              <el-skeleton :rows="0" animated>
                <template slot="template">
                  <!-- news-image 骨架 -->
                  <div class="news-image">
                    <el-skeleton-item variant="rect" style="width: 100%; height: 200px;" />
                  </div>
                  <!-- news-content 骨架 -->
                  <div class="news-content">
                    <el-skeleton-item variant="h3" style="width: 90%; margin-bottom: 10px;" />
                    <el-skeleton-item variant="text" style="width: 60%;" />
                  </div>
                </template>
              </el-skeleton>
            </div>
          </el-col>
        </el-row>
        <el-row v-else :gutter="30">
          <el-col :xs="24" :sm="12" :md="8" v-for="news in newsList" :key="news.id">
            <div class="news-card" @click="goToNewsDetail(news.id)">
              <div class="news-image">
                <img :src="getImageUrl(news.image)" alt="">
              </div>
              <div class="news-content">
                <h3>{{ news.title }}</h3>
                <p class="news-time">{{ news.time }}</p>
              </div>
            </div>
          </el-col>
        </el-row>
        <div class="text-center" style="margin-top: 30px;">
          <el-button type="primary" @click="$router.push('/news')">查看更多资讯</el-button>
        </div>
      </div>
    </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import Header from '../components/Header.vue'
import Footer from '../components/Footer.vue'
import QuoteForm from '../components/QuoteForm.vue'
import { getBanners, getCases, getNews } from '../api'
import { getImageUrl } from '../utils/image'

export default {
  name: 'Home',
  components: {
    Header,
    Footer,
    QuoteForm
  },
  data() {
    return {
      carouselHeight: '500px',
      banners: [],
      services: [
        {
          id: 1,
          title: '私家车托运',
          desc: '让个人出行更高效便捷',
          insurance: 20,
          icon: 'el-icon-truck',
          image: '/私家车.jpg'
        },
        {
          id: 2,
          title: '新车托运',
          desc: '快速运车，轻松管车',
          insurance: 20,
          icon: 'el-icon-goods',
          image: '/新车.jpg'
        },
        {
          id: 3,
          title: '豪车托运',
          desc: '快捷发车，量身定制托运方案',
          insurance: 100,
          icon: 'el-icon-star-on',
          image: '/豪车.jpg'
        },
        {
          id: 4,
          title: '展车托运',
          desc: '价格透明，不限车辆数量',
          insurance: 20,
          icon: 'el-icon-shopping-cart-full',
          image: '/展台车.jpg'
        }
      ],
      advantages: [
        {
          id: 1,
          number: '01',
          title: '多年托运经验',
          desc: '品牌源于2017年，专注轿车托运！',
          image: '/01.jpg'
        },
        {
          id: 2,
          number: '02',
          title: '客服一对一服务',
          desc: '一对一的管家式贴心服务，实时解答运车疑问',
          image: '/02.jpg'
        },
        {
          id: 3,
          number: '03',
          title: '上门提送',
          desc: '上门提车，送车上门，省时、省心、省事！',
          image: '/03.jpg'
        },
        {
          id: 4,
          number: '04',
          title: '全程保险',
          desc: '与人保保险公司合作，车辆安全有保障',
          image: '/04.jpg'
        },
        {
          id: 5,
          number: '05',
          title: '全国网点覆盖',
          desc: '运输干线网络辐射全国，业务辐射全国各地',
          image: '/05.jpg'
        },
        {
          id: 6,
          number: '06',
          title: '实时跟踪',
          desc: '全程GPS定位跟踪，随时了解车辆位置',
          image: '/06.jpg'
        }
      ],
      cases: [],
      newsList: [],
      loadingBanners: true,
      loadingCases: true,
      loadingNews: true,
      carouselHeight: '500px'
    }
  },
  async mounted() {
    await this.loadData()
    this.setCarouselHeight()
    window.addEventListener('resize', this.setCarouselHeight)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setCarouselHeight)
  },
  methods: {
    async loadData() {
      try {
        // 加载轮播图
        const bannerRes = await getBanners()
        if (bannerRes.data && bannerRes.data.length > 0) {
          this.banners = bannerRes.data
        } else {
          // 默认轮播图
          this.banners = [
            {
              title: '专业汽车托运服务',
              subtitle: '安全、高效、便捷',
              image: 'https://via.placeholder.com/1920x500'
            },
            {
              title: '全国400+城市覆盖',
              subtitle: '门到门一站式服务',
              image: 'https://via.placeholder.com/1920x500'
            }
          ]
        }
      } catch (error) {
        console.error('加载轮播图失败', error)
      } finally {
        this.loadingBanners = false
      }

      try {
        // 加载案例
        const casesRes = await getCases()
        if (casesRes.data && casesRes.data.length > 0) {
          this.cases = casesRes.data.slice(0, 4) // 只显示前4个
        } else {
          // 默认案例
          this.cases = [
            {
              id: 1,
              from: '上海',
              to: '青岛',
              days: 2,
              car: '大众帕萨特',
              value: 20,
              comment: '速度很快，两天就到了，服务也很周到，车送达之前就通知什么时候取车'
            }
          ]
        }
      } catch (error) {
        console.error('加载案例失败', error)
      } finally {
        this.loadingCases = false
      }

      try {
        // 加载新闻
        const newsRes = await getNews({ page: 1, pageSize: 3 })
        if (newsRes.data && newsRes.data.list && newsRes.data.list.length > 0) {
          this.newsList = newsRes.data.list
        } else {
          // 默认新闻
          this.newsList = [
            {
              id: 1,
              title: '百万豪车出行新选择，小板车托运解决方案',
              time: '2024-01-15',
              image: 'https://via.placeholder.com/300x200'
            }
          ]
        }
      } catch (error) {
        console.error('加载新闻失败', error)
      } finally {
        this.loadingNews = false
      }
    },
    goToServices() {
      // 如果已经在首页，直接滚动到报价表单
      if (this.$route.path === '/') {
        this.$nextTick(() => {
          const quoteSection = document.querySelector('.quote-section')
          if (quoteSection) {
            quoteSection.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        })
      } else {
        // 如果不在首页，先跳转到首页，然后滚动
        this.$router.push('/').then(() => {
          this.$nextTick(() => {
            const quoteSection = document.querySelector('.quote-section')
            if (quoteSection) {
              quoteSection.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
          })
        })
      }
    },
    getImageUrl, // 使用统一的工具函数
    handleImageError(event) {
      console.error('图片加载失败:', event.target.style.backgroundImage)
      // 可以设置一个默认图片
      event.target.style.backgroundImage = 'url(https://via.placeholder.com/1920x500)'
    },
    goToNewsDetail(id) {
      this.$router.push(`/news/${id}`)
    },
    handleAdvantageImageError(event) {
      const img = event.target
      const currentSrc = img.src || img.getAttribute('src')
      
      // 如果已经是占位图或者已经处理过错误，就不再处理，避免无限循环
      if (currentSrc && (
        currentSrc.includes('via.placeholder.com') || 
        currentSrc.includes('placeholder') ||
        img.dataset.errorHandled === 'true'
      )) {
        // 隐藏图片，显示一个默认的占位符
        img.style.display = 'none'
        const parent = img.parentElement
        if (parent && !parent.querySelector('.image-placeholder')) {
          const placeholder = document.createElement('div')
          placeholder.className = 'image-placeholder'
          placeholder.style.cssText = 'width: 100%; height: 260px; background: #f5f7fa; display: flex; align-items: center; justify-content: center; color: #909399; font-size: 14px;'
          placeholder.textContent = '图片加载失败'
          parent.appendChild(placeholder)
        }
        return
      }
      
      // 标记已经处理过错误
      img.dataset.errorHandled = 'true'
      
      // 尝试使用占位图
      const placeholderUrl = 'https://via.placeholder.com/460x260'
      console.error('服务优势图片加载失败:', currentSrc)
      
      // 如果当前URL不是占位图，才尝试设置占位图
      if (!currentSrc || !currentSrc.includes('via.placeholder.com')) {
        img.src = placeholderUrl
      }
    },
    handleAdvantageImageLoad(event) {
      // 图片加载成功，移除错误标记
      const img = event.target
      img.dataset.errorHandled = 'false'
      img.style.display = 'block'
      
      // 移除占位符
      const parent = img.parentElement
      if (parent) {
        const placeholder = parent.querySelector('.image-placeholder')
        if (placeholder) {
          placeholder.remove()
        }
      }
    },
    setCarouselHeight() {
      if (window.innerWidth <= 768) {
        this.carouselHeight = '300px'
      } else if (window.innerWidth <= 480) {
        this.carouselHeight = '200px'
      } else {
        this.carouselHeight = '500px'
      }
    }
  }
}
</script>

<style scoped>
.home {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.home-content {
  flex: 1; /* 让内容区域占据剩余空间 */
  display: flex;
  flex-direction: column;
}

.banner-skeleton {
  width: 100%;
  background: #f5f7fa;
}

.banner-item {
  height: 100%;
  min-height: 200px;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}


.banner-content {
  position: relative;
  z-index: 1;
  text-align: center;
  color: #fff;
}

.banner-content h2 {
  font-size: 48px;
  margin-bottom: 20px;
}

.banner-content p {
  font-size: 24px;
}

@media (max-width: 768px) {
  .banner-content h2 {
    font-size: 28px;
    margin-bottom: 15px;
  }

  .banner-content p {
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .banner-content h2 {
    font-size: 20px;
    margin-bottom: 10px;
  }

  .banner-content p {
    font-size: 14px;
  }
}

.quote-section {
  background: #f5f5f5;
  padding: 60px 0 0;
  margin-top: -50px;
  position: relative;
  z-index: 10;
}

@media (max-width: 768px) {
  .quote-section {
    padding: 40px 0;
    margin-top: -30px;
  }
}

@media (max-width: 480px) {
  .quote-section {
    padding: 30px 0;
    margin-top: -20px;
  }
}

.services-section,
.process-section,
.advantages-section,
.cases-section,
.news-section {
  padding: 60px 0;
  flex: 1; /* 让内容区域占据剩余空间 */
}

@media (max-width: 768px) {
  .services-section,
  .process-section,
  .advantages-section,
  .cases-section,
  .news-section {
    padding: 40px 0;
  }
}

@media (max-width: 480px) {
  .services-section,
  .process-section,
  .advantages-section,
  .cases-section,
  .news-section {
    padding: 30px 0;
  }
}

.services-section {
  background: #f5f7fa;
}

.service-card {
  background: #fff;
  padding: 30px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  transition: transform 0.3s;
  margin-bottom: 30px;
}

@media (max-width: 768px) {
  .service-card {
    padding: 20px;
    margin-bottom: 20px;
  }
}

@media (max-width: 480px) {
  .service-card {
    padding: 15px;
    margin-bottom: 15px;
  }
}

.service-card:hover {
  transform: translateY(-5px);
}

.service-image {
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

.service-image img {
  width: 310px;
  height: 180px;
  object-fit: cover;
  border-radius: 4px;
}

.service-card h3 {
  font-size: 20px;
  margin-bottom: 15px;
  color: #333;
}

@media (max-width: 768px) {
  .service-icon {
    font-size: 36px;
    margin-bottom: 15px;
  }

  .service-card h3 {
    font-size: 18px;
    margin-bottom: 10px;
  }
}

@media (max-width: 480px) {
  .service-icon {
    font-size: 32px;
    margin-bottom: 10px;
  }

  .service-card h3 {
    font-size: 16px;
  }
}

.service-card p {
  color: #666;
  margin-bottom: 15px;
}

.service-feature {
  background: #f0f9ff;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
  color: #409EFF;
  font-size: 14px;
}

.process-section {
  background: #fff;
}

@media (max-width: 768px) {
  .process-section >>> .el-steps {
    padding: 0 10px;
  }

  .process-section >>> .el-step__title {
    font-size: 12px;
  }

  .process-section >>> .el-step__description {
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .process-section >>> .el-steps {
    padding: 0 5px;
  }

  .process-section >>> .el-step__title {
    font-size: 11px;
  }

  .process-section >>> .el-step__description {
    display: none;
  }
}

.advantages-section {
  background: #f5f7fa;
}

.advantage-item {
  text-align: center;
  padding: 0;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  transition: transform 0.3s;
  margin-bottom: 30px;
}

.advantage-item:hover {
  transform: translateY(-5px);
}

.advantage-image {
  width: 100%;
  height: 260px;
  overflow: hidden;
  background: #f5f7fa;
}

.advantage-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.advantage-item h3 {
  font-size: 20px;
  margin: 20px 20px 15px;
  color: #333;
}

.advantage-item p {
  color: #666;
  line-height: 1.8;
  margin: 0 20px 20px;
  font-size: 14px;
}

@media (max-width: 768px) {
  .advantage-item {
    margin-bottom: 20px;
  }

  .advantage-image {
    height: 200px;
  }

  .advantage-item h3 {
    font-size: 18px;
    margin: 15px 15px 10px;
  }

  .advantage-item p {
    margin: 0 15px 15px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .advantage-item {
    margin-bottom: 15px;
  }

  .advantage-image {
    height: 180px;
  }

  .advantage-item h3 {
    font-size: 16px;
    margin: 12px 12px 8px;
  }

  .advantage-item p {
    margin: 0 12px 12px;
    font-size: 12px;
  }
}

.cases-section {
  background: #fff;
}

.case-card {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  transition: box-shadow 0.3s;
}

@media (max-width: 768px) {
  .case-card {
    padding: 15px;
    margin-bottom: 20px;
  }
}

@media (max-width: 480px) {
  .case-card {
    padding: 12px;
    margin-bottom: 15px;
  }
}

.case-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.case-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e4e7ed;
}

.case-route {
  font-weight: bold;
  color: #409EFF;
}

.case-time {
  color: #999;
  font-size: 14px;
}

.case-info {
  margin-bottom: 15px;
}

.case-car {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 5px;
}

.case-value {
  color: #666;
  font-size: 14px;
}

.case-comment {
  background: #f5f7fa;
  padding: 15px;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  line-height: 1.6;
}

.news-section {
  background: #f5f7fa;
}

.news-card {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  margin-bottom: 30px;
  cursor: pointer;
  transition: transform 0.3s;
}

.news-card:hover {
  transform: translateY(-5px);
}

.news-image {
  width: 100%;
  height: 200px;
  overflow: hidden;
}

@media (max-width: 768px) {
  .news-image {
    height: 150px;
  }
}

@media (max-width: 480px) {
  .news-image {
    height: 120px;
  }
}

.news-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.news-content {
  padding: 20px;
}

.news-content h3 {
  font-size: 18px;
  margin-bottom: 10px;
  color: #333;
  line-height: 1.5;
}

.news-time {
  color: #999;
  font-size: 14px;
}

.text-center {
  text-align: center;
}

/* 移动端容器适配 */
@media (max-width: 768px) {
  .container {
    padding: 0 15px;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 10px;
  }
}
</style>

